import { Headline3, Meta } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import useGetPoolingInfo from 'hooks/useGetPoolingInfo';
import useParkingPricingInfo from 'hooks/useParkingPricingInfo';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useActiveElementStore } from 'stores/activeElementStore';
import styled from 'styled-components/native';
import {
  AccessPermitPoolingGroupCombinedInfo,
  Maybe,
  PoolParking,
  SegmentProps,
} from 'types/generatedSchemaTypes';
import { Row } from '../styles/styles';

const ColumnWithGap = styled.View`
  flex-direction: column;
  gap: 20px;
`;

const BoldMeta = styled(Meta)`
  font-weight: 700;
`;

type PoolingInfo = {
  poolParkingType: 'INSIDE' | 'OUTSIDE';
  poolingGroup: Maybe<AccessPermitPoolingGroupCombinedInfo> | undefined;
  segment: SegmentProps | undefined;
};

const PoolingInfo = ({
  poolParkingType,
  poolingGroup,
  segment,
}: PoolingInfo) => {
  const { t } = useTranslation();
  const { insidePool, outsidePool } = poolingGroup ?? {};
  return (
    <>
      <Row>
        <Headline3>{t('parking.tariff.pooling')}</Headline3>
        <Icon name={poolParkingType === 'INSIDE' ? 'check' : 'close'} />
      </Row>
      {segment?.title && <BoldMeta>{segment?.title}</BoldMeta>}
      <ColumnWithGap>
        <Meta>
          {getPaymentText(
            poolParkingType === 'INSIDE' ? insidePool : outsidePool
          )}
        </Meta>
        <Meta>
          {getSegmentNotes(
            poolParkingType === 'INSIDE' ? insidePool : outsidePool,
            segment
          )}
        </Meta>
      </ColumnWithGap>
    </>
  );
};

const getPaymentText = (
  poolParking: Maybe<PoolParking> | undefined
): string => {
  if (poolParking === 'USER_PAYS') {
    return i18next.t('permit.access.userPaysTariff');
  }
  if (poolParking === 'COMPANY_PAYS') {
    return i18next.t('permit.access.companyPays');
  }
  if (poolParking === 'FORBIDDEN') {
    return i18next.t('permit.access.forbidden');
  }
  if (poolParking === 'FREE') {
    return i18next.t('permit.access.free');
  }
  return i18next.t('permit.access.priceDependsContactEmployer');
};

const getSegmentNotes = (
  poolParking: Maybe<PoolParking> | undefined,
  segment: SegmentProps | undefined
) => {
  if (poolParking === 'USER_PAYS' || poolParking === 'COMPANY_PAYS') {
    return segment?.notes?.map((note) => note?.text).join('\n');
  }
};

const PoolingRates = () => {
  const parkingZoneUid = useActiveElementStore((state) => state.activeElement);

  const { poolingGroup } = useGetPoolingInfo(parkingZoneUid);
  const { allSegments: pricingSegments } = useParkingPricingInfo(
    parkingZoneUid,
    true
  );

  const insideSegment = pricingSegments?.find(
    (segment) => segment.uid === poolingGroup?.customerSegmentInsidePool
  );
  const outsideSegment = pricingSegments?.find(
    (segment) => segment.uid === poolingGroup?.customerSegmentOutsidePool
  );

  return (
    <>
      <PoolingInfo
        segment={insideSegment}
        poolingGroup={poolingGroup}
        poolParkingType="INSIDE"
      />
      <PoolingInfo
        segment={outsideSegment}
        poolingGroup={poolingGroup}
        poolParkingType="OUTSIDE"
      />
    </>
  );
};

export default PoolingRates;
